import React, { useState } from 'react';

const TemplateDesignerPage: React.FC = () => {
  const [template, setTemplate] = useState('');
  const [sampleData, setSampleData] = useState('');
  const [preview, setPreview] = useState('');

  const extractPlaceholders = (text: string): string[] => {
    const matches: string[] = [];
    
    // Extract URL fields from hyperlinks [text](<<URL_Field>>)
    const linkRegex = /\[([^\]]+)\]\(<<\s*([A-Za-z0-9_\-.\s]+)\s*>>\)/g;
    let linkMatch;
    while ((linkMatch = linkRegex.exec(text)) !== null) {
      const key = linkMatch[2].trim();
      if (!matches.includes(key)) {
        matches.push(key);
      }
    }
    
    // Extract regular placeholders
    const regex = /<<\s*([A-Za-z0-9_\-.\s]+)\s*>>/g;
    let match;
    while ((match = regex.exec(text)) !== null) {
      const key = match[1].trim();
      if (!matches.includes(key)) {
        matches.push(key);
      }
    }
    
    return matches;
  };

  const renderPreview = () => {
    if (!template || !sampleData) return;
    
    try {
      const data = JSON.parse(sampleData);
      
      // First, handle hyperlinks with format [text](<<URL_Field>>)
      let rendered = template.replace(/\[([^\]]+)\]\(<<\s*([A-Za-z0-9_\-.\s]+)\s*>>\)/g, (match, linkText, urlKey) => {
        const key = urlKey.trim();
        const url = data[key];
        if (url === null || url === undefined || url === '') {
          return linkText; // Just return the text if no URL
        }
        return `${linkText} ${String(url)}`; // SMS format: "text url"
      });
      
      // Then handle regular placeholders
      rendered = rendered.replace(/<<\s*([A-Za-z0-9_\-.\s]+)\s*>>/g, (match, key) => {
        const trimmedKey = key.trim();
        return data[trimmedKey] !== undefined ? String(data[trimmedKey]) : match;
      });
      
      setPreview(rendered);
    } catch (error) {
      setPreview('Error: Invalid JSON in sample data');
    }
  };

  const insertPlaceholder = (fieldName: string) => {
    const placeholder = `<<${fieldName}>>`;
    setTemplate(prev => prev + placeholder);
  };

  const placeholders = extractPlaceholders(template);

  const sampleDataTemplate = placeholders.reduce((acc, field) => {
    acc[field] = `sample_${field.toLowerCase().replace(/\s+/g, '_')}`;
    return acc;
  }, {} as Record<string, string>);

  return (
    <div>
      <h1 className="page-title">Template Designer</h1>
      
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
        <div className="card">
          <h3>Message Template</h3>
          <div className="form-group">
            <label htmlFor="template">Template Text</label>
            <textarea
              id="template"
              value={template}
              onChange={(e) => setTemplate(e.target.value)}
              placeholder="Hello <<Name>>, your order #<<Order ID>> has been shipped to <<Address>>!"
              style={{ minHeight: '200px' }}
            />
            <small style={{ color: '#666', display: 'block', marginTop: '0.5rem' }}>
              Use &lt;&lt;Field Name&gt;&gt; format for dynamic fields
            </small>
          </div>

          <div>
            <h4>Quick Insert Common Fields</h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
              {['Name', 'First Name', 'Last Name', 'Phone', 'Email', 'Order ID', 'Tracking Number', 'Address'].map(field => (
                <button
                  key={field}
                  type="button"
                  className="btn btn-secondary"
                  style={{ fontSize: '0.875rem', padding: '0.5rem' }}
                  onClick={() => insertPlaceholder(field)}
                >
                  + {field}
                </button>
              ))}
            </div>
            
            <h4>Quick Insert Hyperlinks</h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
              <button
                type="button"
                className="btn btn-secondary"
                style={{ fontSize: '0.875rem', padding: '0.5rem' }}
                onClick={() => setTemplate(prev => prev + '[Track your order](<<Tracking URL>>)')}
              >
                + Tracking Link
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                style={{ fontSize: '0.875rem', padding: '0.5rem' }}
                onClick={() => setTemplate(prev => prev + '[View details](<<Order URL>>)')}
              >
                + Order Link
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                style={{ fontSize: '0.875rem', padding: '0.5rem' }}
                onClick={() => setTemplate(prev => prev + '[Contact support](<<Support URL>>)')}
              >
                + Support Link
              </button>
            </div>
          </div>

          {placeholders.length > 0 && (
            <div>
              <h4>Detected Placeholders</h4>
              <div style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '4px' }}>
                {placeholders.map((field, index) => (
                  <span
                    key={index}
                    style={{
                      display: 'inline-block',
                      background: '#e9ecef',
                      padding: '0.25rem 0.5rem',
                      margin: '0.25rem',
                      borderRadius: '4px',
                      fontSize: '0.875rem'
                    }}
                  >
                    {field}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="card">
          <h3>Preview</h3>
          <div className="form-group">
            <label htmlFor="sampleData">Sample Data (JSON)</label>
            <textarea
              id="sampleData"
              value={sampleData}
              onChange={(e) => setSampleData(e.target.value)}
              placeholder={JSON.stringify(sampleDataTemplate, null, 2)}
              style={{ minHeight: '150px', fontFamily: 'monospace' }}
            />
            <small style={{ color: '#666', display: 'block', marginTop: '0.5rem' }}>
              Provide sample data to see how your template will render
            </small>
          </div>

          <button className="btn" onClick={renderPreview}>
            Generate Preview
          </button>

          {preview && (
            <div style={{ marginTop: '1rem' }}>
              <h4>Rendered Message</h4>
              <div style={{
                background: '#f8f9fa',
                padding: '1rem',
                borderRadius: '4px',
                border: '1px solid #dee2e6',
                whiteSpace: 'pre-wrap',
                fontFamily: 'system-ui'
              }}>
                {preview}
              </div>
              <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#666' }}>
                Character count: {preview.length}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="card" style={{ marginTop: '2rem' }}>
        <h3>Template Guidelines</h3>
        <ul style={{ margin: '1rem 0', paddingLeft: '2rem' }}>
          <li><strong>Placeholders:</strong> Use &lt;&lt;Field Name&gt;&gt; format (case-sensitive)</li>
          <li><strong>Hyperlinks:</strong> Use [Link Text](&lt;&lt;URL Field&gt;&gt;) format</li>
          <li>🔗 <strong>URLs are automatically shortened</strong> using TinyURL to save characters</li>
          <li>Field names should match exactly with your CSV column headers</li>
          <li>SMS messages are typically limited to 160 characters per segment</li>
          <li>Test your template with sample data before sending to contacts</li>
          <li>Placeholders that don't match any data will remain as-is in the final message</li>
        </ul>
        
        <h4>Hyperlink Examples</h4>
        <div style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '4px', marginTop: '1rem' }}>
          <div style={{ fontFamily: 'monospace', marginBottom: '0.5rem' }}>
            <strong>Template:</strong> Hello &lt;&lt;Name&gt;&gt;, [track your package](&lt;&lt;Tracking URL&gt;&gt;)
          </div>
          <div style={{ fontFamily: 'monospace', marginBottom: '0.5rem' }}>
            <strong>Sample Data:</strong> {`{"Name": "John", "Tracking URL": "https://track.com/123"}`}
          </div>
          <div style={{ fontFamily: 'monospace', color: '#28a745' }}>
            <strong>Result:</strong> Hello John, track your package https://tinyurl.com/abc123
          </div>
          <div style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.5rem' }}>
            <em>Note: Long URLs are automatically shortened to save SMS characters</em>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateDesignerPage;
